import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";

var _class, _class2, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _class3;

import { getUniqueId } from '@udemy/design-system-utils';
import { omitI18nProps, useI18n, withI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { IconButton } from '@udemy/react-core-components';
import { Loader } from '@udemy/react-reveal-components';
import { debounce, noop } from '@udemy/shared-utils';
import classNames from 'classnames';
import { action, computed, observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import React, { Children } from 'react';
import UAParser from 'ua-parser-js';
import { isElementVisibleInScrollPort } from './carousel-visibility-utils';
import styles from "./carousel.module.css";
export const scrollPortDataAttribute = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  prop: {
    'data-carousel-scroll-port': true
  },
  selector: '[data-carousel-scroll-port]'
};
export const nextButtonDataAttribute = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  prop: {
    'data-pager-type': 'next'
  },
  selector: '[data-pager-type="next"]'
};
export const prevButtonDataAttribute = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  prop: {
    'data-pager-type': 'prev'
  },
  selector: '[data-pager-type="prev"]'
};
/** Number of milliseconds to debounce the {@link Carousel} scrollHandler */

export const CAROUSEL_ON_SCROLL_DEBOUNCE_INTERVAL = 100;
/** Names of events to listen to, to allow external pager control */

export const CAROUSEL_NEXT_PAGE_EVENT = 'CarouselNextPageEvent';
export const CAROUSEL_PREV_PAGE_EVENT = 'CarouselPrevPageEvent';
/** Utility functions to make external page control easier to integrate. */

export const dispatchCarouselNextPageEvent = eventTarget => {
  eventTarget.dispatchEvent(new Event(CAROUSEL_NEXT_PAGE_EVENT));
};
export const dispatchCarouselPreviousPageEvent = eventTarget => {
  eventTarget.dispatchEvent(new Event(CAROUSEL_PREV_PAGE_EVENT));
};
export const CarouselPreviousButton = _ref => {
  let {
    className,
    pagerButtonSize,
    onClick,
    disabled,
    udStyle = 'white-solid'
  } = _ref;
  return /*#__PURE__*/React.createElement(IconButton, Object.assign({
    className: classNames('ud-carousel-pager-button', 'ud-carousel-pager-button-prev', className, styles.button, styles['prev-button']),
    size: pagerButtonSize !== null && pagerButtonSize !== void 0 ? pagerButtonSize : Carousel.defaultProps.pagerButtonSize,
    udStyle: udStyle,
    round: true,
    onClick: onClick
  }, prevButtonDataAttribute.prop, {
    disabled: disabled
  }), /*#__PURE__*/React.createElement(CarouselPreviousIcon, null));
};
export const CarouselNextButton = _ref2 => {
  let {
    className,
    pagerButtonSize,
    onClick,
    disabled,
    isLoading,
    udStyle = 'white-solid'
  } = _ref2;
  const nextIcon = isLoading ? /*#__PURE__*/React.createElement(Loader, {
    color: "inherit"
  }) : /*#__PURE__*/React.createElement(CarouselNextIcon, null);
  return /*#__PURE__*/React.createElement(IconButton, Object.assign({
    className: classNames('ud-carousel-pager-button', 'ud-carousel-pager-button-next', className, styles.button, styles['next-button']),
    size: pagerButtonSize !== null && pagerButtonSize !== void 0 ? pagerButtonSize : Carousel.defaultProps.pagerButtonSize,
    udStyle: udStyle,
    round: true,
    onClick: onClick
  }, nextButtonDataAttribute.prop, {
    disabled: disabled
  }), nextIcon);
};
/**
 * Icon for button to navigate to next set of children.
 *
 * @remarks
 * Separated as a functional component to use i18n hook.
 */

const CarouselNextIcon = () => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Icon, {
    glyph: "next",
    size: "large",
    color: "inherit",
    label: gettext('Next')
  });
};
/**
 * Icon for button to navigate to previous set of slides.
 *
 * @remarks
 * Separated as a functional component to use i18n hook.
 */


const CarouselPreviousIcon = () => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Icon, {
    glyph: "previous",
    size: "large",
    color: "inherit",
    label: gettext('Previous')
  });
};
/** Event dispatched when the carousel mounts, when the slide nodes change, or when the user scrolls the carousel. */


/**
 * HTML `section` wrapper used to infer region role to Carousel.
 *
 */
const CarouselRegion = _ref3 => {
  let {
    ariaLabel,
    ariaRoleDescription,
    children,
    containerRef
  } = _ref3;
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement("section", {
    ref: containerRef // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    ,
    "aria-label": ariaLabel || gettext('Carousel'),
    "aria-roledescription": ariaRoleDescription,
    className: styles.container
  }, children);
};
/** Parameter object passed to {@link CarouselProps.getStatusAnnouncement}. */


export const defaultGetStatusAnnouncement = _ref4 => {
  let {
    ninterpolate,
    slides
  } = _ref4;
  const visibleIndices = slides.reduce((indices, child, index) => {
    if (child.getAttribute('aria-hidden') === 'false') {
      return [...indices, index + 1];
    }

    return indices;
  }, []);

  if (visibleIndices.length === 0) {
    return '';
  }

  return ninterpolate('Item %(indices)s of %(total)s', 'Items %(indices)s of %(total)s', visibleIndices.length, {
    indices: visibleIndices.join(', '),
    total: slides.length
  });
};
/**
 * The Carousel component.
 *
 * @remarks
 *
 * Pass slides via the `slides` prop, each wrapped in a `CarouselSlide`.
 *
 * This component supports both scrolling and pagination.
 * To ensure the best pagination experience, rather than rendering a list of items, render a list of pages.
 * Each page should contain logic to responsively render its own items.
 *
 * If you must render a list of items, in order for pagination to work reliably,
 * i.e., not inadvertently skip items, make sure that the scrollport width is a multiple of the scroll item width, including margins.
 *
 * ex. scrollport width = 500px; scrollitem width = 250px (including borders, margins)
 *
 * This is because we rely on scrollsnap CSS property to align the scroll items after the paging (element.scrollBy) operation,
 * which simply scrolls the scrollport element in the horizontal direction by its own clientWidth.
 *
 * Since scroll snap picks the closest element to snap to the edge,
 * it can potentially skip an element when it lies in the "grey area" between the pages.
 *
 * This component also supports external control of pager state, by listening to events on the given `pagerEventTarget`.
 * Simply pass in an event target, and then use the exported `dispatchCarouselNextPageEvent` or `dispatchCarouselPreviousPageEvent` functions.
 */

export let CarouselBase = observer(_class = (_class2 = (_class3 = class CarouselBase extends React.Component {
  constructor(props) {
    var _this$props$id;

    super(props);
    this.applyIOSScrollHackProperty = void 0;
    this.resetIOSScrollHackProperty = void 0;
    this.destroy = void 0;
    this.lastChild = void 0;
    this.scrollPortRef = void 0;
    this.scrollBehavior = void 0;
    this.id = void 0;

    _initializerDefineProperty(this, "scroll", _descriptor, this);

    _initializerDefineProperty(this, "isLoading", _descriptor2, this);

    _initializerDefineProperty(this, "ariaLiveAnnouncement", _descriptor3, this);

    _initializerDefineProperty(this, "setIsLoading", _descriptor4, this);

    this.handleLastChildEnter = async isLastPage => {
      if (!isLastPage) {
        return;
      }

      const {
        children,
        onLoadMore,
        slides
      } = this.props; // Select last child from normalized array of both methods for defining slides

      const lastChild = [...Children.toArray(children), ...Children.toArray(slides)].at(-1);

      if (this.lastChild !== lastChild) {
        this.lastChild = lastChild;
        await (onLoadMore === null || onLoadMore === void 0 ? void 0 : onLoadMore());
      }
    };

    this.handleCustomNextClick = () => {
      this.handleNextClick();
    };

    this.handleCustomPrevClick = () => {
      this.handlePrevClick();
    };

    this.handleScroll = async isNextDirection => {
      var _this$scrollPortRef$c;

      const {
        clientWidth,
        scrollWidth,
        scrollLeft
      } = this.scrollPortRef.current;
      const scrollAmount = this.props.pageByFullWidth ? clientWidth : 0.8 * clientWidth;
      const isRtl = this.isRtl; // Call the appropriate event handler

      if (isNextDirection) {
        var _this$props$onNextCli, _this$props;

        (_this$props$onNextCli = (_this$props = this.props).onNextClick) === null || _this$props$onNextCli === void 0 ? void 0 : _this$props$onNextCli.call(_this$props);
      } else {
        var _this$props$onPreviou, _this$props2;

        (_this$props$onPreviou = (_this$props2 = this.props).onPreviousClick) === null || _this$props$onPreviou === void 0 ? void 0 : _this$props$onPreviou.call(_this$props2);
      } // Calculate scroll direction based on RTL setting and direction of movement
      // In RTL: next = negative (left), prev = positive (right)
      // In LTR: next = positive (right), prev = negative (left)


      const scrollDirection = isNextDirection ? 1 : -1;
      const scrollByValue = isRtl ? -scrollDirection * scrollAmount : scrollDirection * scrollAmount; // Calculate remaining scroll amount for loading more

      let scrollAmountRemaining;

      if (isRtl) {
        // In RTL, scrollLeft is negative and increases toward zero as you scroll right
        scrollAmountRemaining = scrollWidth - (-scrollLeft + clientWidth);
      } else {
        scrollAmountRemaining = scrollWidth - (scrollLeft + clientWidth);
      }

      if (this.props.onLoadMore && scrollAmountRemaining < Math.abs(scrollAmount)) {
        this.setIsLoading(true);
        await this.props.onLoadMore();
      }

      this.setIsLoading(false);
      this.applyIOSScrollHackProperty();
      (_this$scrollPortRef$c = this.scrollPortRef.current) === null || _this$scrollPortRef$c === void 0 ? void 0 : _this$scrollPortRef$c.scrollBy({
        left: scrollByValue,
        behavior: this.scrollBehavior
      });
    };

    this.handleNextClick = async () => {
      await this.handleScroll(true);
    };

    this.handlePrevClick = async () => {
      await this.handleScroll(false);
    };

    this.updateSlideAriaHidden = () => {
      const scrollPort = this.scrollPortRef.current;

      if (!(scrollPort !== null && scrollPort !== void 0 && scrollPort.getBoundingClientRect) || !scrollPort.children) {
        return;
      }

      const scrollPortRect = scrollPort.getBoundingClientRect();
      const children = Array.from(scrollPort.children);
      children.forEach(child => {
        const element = child;

        if (!(element !== null && element !== void 0 && element.setAttribute) || !(element !== null && element !== void 0 && element.getBoundingClientRect)) {
          return;
        }

        const isVisible = isElementVisibleInScrollPort(element, scrollPortRect);
        element.setAttribute('aria-hidden', String(!isVisible));
      });
    };

    _initializerDefineProperty(this, "handleCarouselMutation", _descriptor5, this);

    this.scrollHandler = debounce(this.handleCarouselMutation, CAROUSEL_ON_SCROLL_DEBOUNCE_INTERVAL);
    this.scrollPortRef = /*#__PURE__*/React.createRef();
    this.id = (_this$props$id = this.props.id) !== null && _this$props$id !== void 0 ? _this$props$id : getUniqueId('scroll-port');
    this.destroy = noop;
    this.scrollBehavior = 'smooth';
    const isIOS = new UAParser().getOS().name === 'iOS'; // As of 2025-01-29, iOS does not honor the `scroll-snap` property when
    // we use `scrollBy` with `overflow-x: auto`. Imprecise scrolling by `scrollBy`
    // when pageByFullWidth and gridFullWidthItems=true, for example, results in
    // unwanted misalignment with scroll items and the scroll port. To work around this,
    // For button presses, temporarily change the overflow property, and then remove it
    // after the scroll completes. Note that Firefox, for example, does not work well
    // with this hack, so we only apply it to iOS.

    this.applyIOSScrollHackProperty = () => {
      if (isIOS) {
        var _this$scrollPortRef$c2;

        (_this$scrollPortRef$c2 = this.scrollPortRef.current) === null || _this$scrollPortRef$c2 === void 0 ? void 0 : _this$scrollPortRef$c2.style.setProperty('overflow-x', 'hidden');
      }
    };

    this.resetIOSScrollHackProperty = () => {
      if (isIOS) {
        var _this$scrollPortRef$c3;

        (_this$scrollPortRef$c3 = this.scrollPortRef.current) === null || _this$scrollPortRef$c3 === void 0 ? void 0 : _this$scrollPortRef$c3.style.removeProperty('overflow-x');
      }
    };
  }

  componentDidMount() {
    if (this.props.onLoadMore) {
      this.destroy = reaction(() => this.isLastPage, this.handleLastChildEnter);
    }

    this.addPageEventListeners(this.props.pagerEventTarget);
    this.handleCarouselMutation();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.children !== this.props.children || prevProps.slides !== this.props.slides) {
      this.handleCarouselMutation();
    }

    if (prevProps.pagerEventTarget !== this.props.pagerEventTarget) {
      this.removePageEventListeners(prevProps.pagerEventTarget);
      this.addPageEventListeners(this.props.pagerEventTarget);
    }
  }

  componentWillUnmount() {
    this.removePageEventListeners(this.props.pagerEventTarget);
    this.destroy();
  }

  /**
   * Computes the gap in the grid for a grid with full-width items.
   * This is used to adjust page index calculations to account for the gap.
   */
  get gridGapForFullWidthItems() {
    // if gridFullWidthItems is set to true, the scroll x has gridGap added to it
    if (this.props.gridFullWidthItems) {
      switch (this.props.gridSize) {
        case 'large':
          return 24;

        case 'small':
          return 8;

        case 'medium':
        default:
          return 16;
      }
    } // default to 0 it only takes into account gap in calculations when gridFullWidthItems is true;


    return 0;
  }

  get isFirstPage() {
    return this.scroll.x === 0;
  }

  get isRtl() {
    return this.props.direction === 'rtl';
  }

  get isLastPage() {
    const {
      x,
      clientWidth,
      totalWidth
    } = this.scroll;

    if (this.isRtl) {
      // In RTL mode, scrollLeft is negative and increases towards zero as you scroll right. See:
      // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollLeft#scrolling_to_the_left
      return this.round(-x + clientWidth) === this.round(totalWidth);
    } // Round to nearest 5, there can be a small offset due to hidden items / borders


    return this.round(x + clientWidth) === this.round(totalWidth);
  }

  get isPageable() {
    // Usage of nullish coalescing vs OR operator introduces a bug where pager buttons do not appear.
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return this.props.isInfiniteScroll || this.pageCount > 1;
  }

  get pageCount() {
    if (this.props.isInfiniteScroll) {
      return null;
    }

    return Math.ceil(this.scroll.totalWidth / (this.scroll.clientWidth + this.gridGapForFullWidthItems));
  }

  get visiblePageIndex() {
    if (this.props.isInfiniteScroll) {
      return null;
    }

    const {
      x,
      clientWidth
    } = this.scroll;

    if (this.isRtl) {
      // In RTL mode, scrollLeft is negative and increases towards zero as you scroll right
      return Math.ceil(-x / (clientWidth + this.gridGapForFullWidthItems));
    }

    return Math.ceil(x / (clientWidth + this.gridGapForFullWidthItems));
  }

  round(n) {
    let precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 5;
    return Math.round(n / precision) * precision;
  }

  addPageEventListeners(eventTarget) {
    var _eventTarget$addEvent, _eventTarget$addEvent2;

    eventTarget === null || eventTarget === void 0 ? void 0 : (_eventTarget$addEvent = eventTarget.addEventListener) === null || _eventTarget$addEvent === void 0 ? void 0 : _eventTarget$addEvent.call(eventTarget, CAROUSEL_NEXT_PAGE_EVENT, this.handleCustomNextClick);
    eventTarget === null || eventTarget === void 0 ? void 0 : (_eventTarget$addEvent2 = eventTarget.addEventListener) === null || _eventTarget$addEvent2 === void 0 ? void 0 : _eventTarget$addEvent2.call(eventTarget, CAROUSEL_PREV_PAGE_EVENT, this.handleCustomPrevClick);
  }

  removePageEventListeners(eventTarget) {
    var _eventTarget$removeEv, _eventTarget$removeEv2;

    eventTarget === null || eventTarget === void 0 ? void 0 : (_eventTarget$removeEv = eventTarget.removeEventListener) === null || _eventTarget$removeEv === void 0 ? void 0 : _eventTarget$removeEv.call(eventTarget, CAROUSEL_NEXT_PAGE_EVENT, this.handleCustomNextClick);
    eventTarget === null || eventTarget === void 0 ? void 0 : (_eventTarget$removeEv2 = eventTarget.removeEventListener) === null || _eventTarget$removeEv2 === void 0 ? void 0 : _eventTarget$removeEv2.call(eventTarget, CAROUSEL_PREV_PAGE_EVENT, this.handleCustomPrevClick);
  } // Another layer of function calls to make mocking in tests easier.


  updateAriaLiveAnnouncement() {
    var _getStatusAnnouncemen;

    const scrollPort = this.scrollPortRef.current;

    if (!(scrollPort !== null && scrollPort !== void 0 && scrollPort.children)) {
      return;
    }

    const {
      ninterpolate,
      getStatusAnnouncement
    } = this.props;
    const slides = Array.from(scrollPort.children);
    this.ariaLiveAnnouncement = (_getStatusAnnouncemen = getStatusAnnouncement === null || getStatusAnnouncement === void 0 ? void 0 : getStatusAnnouncement({
      ninterpolate,
      slides
    })) !== null && _getStatusAnnouncemen !== void 0 ? _getStatusAnnouncemen : '';
  }

  render() {
    const {
      allowScroll,
      children,
      id,
      onLoadMore,
      onMutation,
      pagerButtonClassName,
      pagerButtonSize,
      className,
      itemClassName,
      smallGrid,
      gridSize,
      showPager,
      isInfiniteScroll,
      gridMode,
      gridFullWidthItems,
      pageByFullWidth,
      ariaLive,
      ariaLabel,
      ariaRoleDescription,
      onNextClick,
      onPreviousClick,
      fullViewport,
      pagerEventTarget,
      containerRef,
      slides,
      getStatusAnnouncement,
      ...rest
    } = omitI18nProps(this.props);
    const pager = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CarouselPreviousButton, {
      className: pagerButtonClassName,
      disabled: isInfiniteScroll ? false : this.isFirstPage,
      onClick: this.handlePrevClick,
      pagerButtonSize: pagerButtonSize
    }), /*#__PURE__*/React.createElement(CarouselNextButton, {
      className: pagerButtonClassName,
      disabled: isInfiniteScroll ? false : this.isLastPage,
      onClick: this.handleNextClick,
      isLoading: this.isLoading,
      pagerButtonSize: pagerButtonSize
    })); // Prefer slides prop, but provide legacy support for rendering
    // slides as wrapped children.

    const slideNodes = slides !== null && slides !== void 0 ? slides : /*#__PURE__*/React.createElement(React.Fragment, null, React.Children.map(children, (child, i) => /*#__PURE__*/React.createElement("div", {
      "data-index": i,
      className: classNames(itemClassName, styles['scroll-item']),
      key: i
    }, child)));
    return /*#__PURE__*/React.createElement(CarouselRegion, {
      ariaLabel: ariaLabel,
      ariaRoleDescription: ariaRoleDescription,
      containerRef: containerRef
    }, /*#__PURE__*/React.createElement("span", {
      className: "ud-sr-only",
      "aria-atomic": true,
      "aria-live": ariaLive
    }, ariaLive === 'polite' ? this.ariaLiveAnnouncement : ''), /*#__PURE__*/React.createElement("div", Object.assign({
      ref: this.scrollPortRef,
      className: classNames(className, styles['scroll-port'], {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        [styles['small-grid']]: smallGrid || gridSize === 'small',
        [styles['large-grid']]: gridSize === 'large',
        [styles.grid]: gridMode,
        [styles['grid-full-width-items']]: gridMode && gridFullWidthItems,
        [styles['scroll-lock']]: !allowScroll,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'ud-full-viewport-container': fullViewport,
        [styles['container-full-viewport']]: fullViewport
      }),
      "data-purpose": 'scroll-port'
    }, scrollPortDataAttribute.prop, rest, {
      id: this.id,
      onScroll: this.scrollHandler
    }), slideNodes), this.isPageable && showPager && pager);
  }

}, _class3.displayName = 'Carousel', _class3.defaultProps = {
  allowScroll: false,
  pagerButtonClassName: undefined,
  pagerButtonSize: 'large',
  pagerEventTarget: undefined,
  className: undefined,
  itemClassName: undefined,
  id: undefined,
  onLoadMore: undefined,
  onMutation: undefined,
  smallGrid: false,
  gridSize: 'medium',
  showPager: true,
  isInfiniteScroll: false,
  gridMode: true,
  gridFullWidthItems: false,
  pageByFullWidth: false,
  ariaLive: 'polite',
  ariaLabel: undefined,
  onNextClick: noop,
  onPreviousClick: noop,
  fullViewport: false,
  getStatusAnnouncement: defaultGetStatusAnnouncement
}, _class3), (_applyDecoratedDescriptor(_class2.prototype, "componentDidMount", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "componentDidMount"), _class2.prototype), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "scroll", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {
      x: 0,
      clientWidth: 0,
      totalWidth: 0
    };
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "isLoading", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "ariaLiveAnnouncement", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class2.prototype, "setIsLoading", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return isLoading => {
      this.isLoading = isLoading;
    };
  }
}), _applyDecoratedDescriptor(_class2.prototype, "gridGapForFullWidthItems", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "gridGapForFullWidthItems"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "isFirstPage", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "isFirstPage"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "isRtl", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "isRtl"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "isLastPage", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "isLastPage"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "isPageable", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "isPageable"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "pageCount", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "pageCount"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "visiblePageIndex", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "visiblePageIndex"), _class2.prototype), _descriptor5 = _applyDecoratedDescriptor(_class2.prototype, "handleCarouselMutation", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      if (this.scrollPortRef.current) {
        var _this$props$onMutatio, _this$props3;

        // Remove property applied before scroll from button click
        this.resetIOSScrollHackProperty();
        const {
          clientWidth,
          scrollWidth,
          scrollLeft
        } = this.scrollPortRef.current;
        this.scroll = {
          x: scrollLeft,
          totalWidth: scrollWidth,
          clientWidth
        };
        this.updateSlideAriaHidden();
        this.updateAriaLiveAnnouncement();
        (_this$props$onMutatio = (_this$props3 = this.props).onMutation) === null || _this$props$onMutatio === void 0 ? void 0 : _this$props$onMutatio.call(_this$props3, {
          isLastPage: this.isLastPage,
          isPageable: this.isPageable,
          pageCount: this.pageCount,
          visiblePageIndex: this.visiblePageIndex
        });
      }
    };
  }
})), _class2)) || _class;
export const Carousel = withI18n(CarouselBase);
//# sourceMappingURL=carousel.react-component.js.map